import { useEffect, useState, type ReactNode } from 'react';

export type IntroMotif = 'bands' | 'arcs' | 'dots';

const svgCls = 'pointer-events-none absolute right-0 top-0 hidden h-[13.5rem] w-[15.75rem] xl:block';

/** Bandes diagonales fines: une large bande très pâle et trois lignes gris-bleu. */
function Bands() {
  const H = 360;
  const line = (t: number) => `${t},0 ${t - H},${H}`;
  return (
    <svg aria-hidden viewBox={`0 0 420 ${H}`} className={svgCls}>
      <polygon className="ih-tri" style={{ ['--i' as string]: 0 }} points={`560,0 470,0 ${470 - H},${H} ${560 - H},${H}`} fill="var(--color-steel-soft)" fillOpacity="0.28" />
      {[470, 430, 390].map((t, n) => (
        <polyline key={t} className="ih-tri" style={{ ['--i' as string]: n + 1 }} points={line(t)} fill="none" stroke="var(--color-steel-soft)" strokeWidth="2" strokeLinecap="square" />
      ))}
    </svg>
  );
}

/** Arcs concentriques dans le coin: trois quarts de cercle fins, le plus grand légèrement rempli. */
function Arcs() {
  return (
    <svg aria-hidden viewBox="0 0 420 360" className={svgCls}>
      <path className="ih-tri" style={{ ['--i' as string]: 0 }} d="M420 0 V300 A300 300 0 0 1 120 0 Z" fill="var(--color-steel-soft)" fillOpacity="0.2" />
      {[220, 150, 80].map((r, n) => (
        <path key={r} className="ih-tri" style={{ ['--i' as string]: n + 1 }} d={`M420 ${r} A${r} ${r} 0 0 0 ${420 - r} 0`} fill="none" stroke="var(--color-steel-soft)" strokeWidth="2" />
      ))}
    </svg>
  );
}

/** Grille de petits points qui s'estompe vers le bas et vers la gauche. */
function Dots() {
  const cols = 9, rows = 7;
  return (
    <svg aria-hidden viewBox="0 0 420 360" className={svgCls}>
      {Array.from({ length: rows }, (_, r) => Array.from({ length: cols }, (_, c) => {
        const fade = 1 - (r / rows) * 0.55 - ((cols - 1 - c) / cols) * 0.55;
        return fade > 0.08 ? <circle key={`${r}-${c}`} className="ih-tri" style={{ ['--i' as string]: (r + c) * 0.25 }} cx={410 - (cols - 1 - c) * 46} cy={20 + r * 46} r="4" fill="var(--color-steel)" fillOpacity={Math.max(0.12, fade * 0.55)} /> : null;
      }))}
    </svg>
  );
}

function Motif({ kind }: { kind: IntroMotif }) {
  return kind === 'arcs' ? <Arcs /> : kind === 'dots' ? <Dots /> : <Bands />;
}

/**
 * En-tête de page de premier niveau, sur fond blanc: petit titre dont le trait part du bord gauche de l'écran,
 * grand titre sur plusieurs lignes qui monte dans un masque, bandes diagonales qui entrent en glissant.
 */
export default function PageIntro({ eyebrow, title, children, motif = 'bands' }: { eyebrow: string; title: string; children?: ReactNode; motif?: IntroMotif }) {
  const [seen, setSeen] = useState(false);
  useEffect(() => {
    const id = requestAnimationFrame(() => setSeen(true));
    return () => cancelAnimationFrame(id);
  }, []);

  return (
    <section data-seen={seen} className="relative overflow-hidden bg-white text-black">
      <Motif kind={motif} />
      <div className="site-container relative py-16 md:py-24 lg:py-[5.25rem]">
        <div className="relative inline-block">
          <span aria-hidden className="ih-line absolute right-[calc(100%+1.5rem)] top-1/2 h-[0.13rem] w-[100vw] -translate-y-1/2 bg-brand" />
          <div className="cs-eyebrow text-xs font-semibold uppercase tracking-[0.0625rem] text-ink"><span className="cs-eyebrow-text inline-block">{eyebrow}</span></div>
        </div>
        <div className="cs-mask mt-6 max-w-4xl pb-2">
          <h1 className="cs-title text-inherit font-display text-[2.75rem] font-bold normal-case leading-[1.02] tracking-normal text-black md:text-[4.5rem]">{title}</h1>
        </div>
        {children && <div className="mt-8 max-w-2xl">{children}</div>}
      </div>
    </section>
  );
}
